/*
 * @Author: mina.min
 * @Date: 2025-01-20 10:00:00
 * @LastEditors: mina.min
 * @LastEditTime: 2025-06-21 18:24:18
 * @FilePath: /OTAManagementSystem/src/pages/FirmwareManagement/OTAUpgrade.tsx
 * @Description: OTA升级组件
 */
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HolderOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  RedoOutlined,
  RocketOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  firmwareApi,
  otaApi,
  type FirmwarePackage,
  type OTAUpgradeItem,
  type VehicleUpgradeStatus,
} from '../../store/firmwareStore'
import './OTAUpgrade.scss'

const { Title } = Typography

// 设备管理API
const deviceApi = {
  // 获取启用状态的设备列表
  async getActiveDeviceList() {
    // 由于设备信息由车辆自动上报，所有设备都被认为是可用的
    return {
      code: 200,
      message: '获取设备列表成功',
      data: [],
    }
  },
}

const DraggableRow: React.FC<{
  index: number
  record: OTAUpgradeItem
  moveRow: (dragIndex: number, hoverIndex: number) => void
  children: React.ReactNode
}> = ({ index, record, moveRow, children }) => {
  const ref = React.useRef<HTMLTableRowElement>(null)

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'row',
    item: { index, record },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    options: {
      dropEffect: 'move',
    },
  })

  const [{ isOver }, drop] = useDrop({
    accept: 'row',
    collect: (monitor: any) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
    hover: (item: { index: number; record: OTAUpgradeItem }, monitor: any) => {
      if (!ref.current) {
        return
      }

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) {
        return
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return

      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      const threshold = hoverMiddleY * 0.3

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY - threshold) {
        return
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY + threshold) {
        return
      }

      moveRow(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  drag(drop(ref))
  preview(ref)

  if (isDragging) {
    return (
      <tr className='drag-placeholder'>
        <td
          colSpan={5}
          style={{
            height: '60px',
            background: 'linear-gradient(90deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
            border: '2px dashed #3b82f6',
            borderRadius: '8px',
            position: 'relative',
            animation: 'pulse 2s infinite',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            拖拽到此处放置
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr
      ref={ref}
      style={{
        cursor: 'grab',
        transition: 'all 0.3s ease',
        transform: isOver ? 'translateY(-2px)' : 'translateY(0)',
        backgroundColor: '#ffffff',
        boxShadow: isOver ? '0 4px 12px rgba(59, 130, 246, 0.15)' : 'none',
      }}
      className={isOver ? 'drop-target-row' : ''}
      onMouseDown={() => {
        if (ref.current) {
          ref.current.style.cursor = 'grabbing'
        }
      }}
      onMouseUp={() => {
        if (ref.current) {
          ref.current.style.cursor = 'grab'
        }
      }}
    >
      {React.Children.map(children, (child, colIndex) => {
        if (colIndex === 0) {
          return (
            <td key={colIndex} style={{ padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span className='drag-handle'>
                  <HolderOutlined />
                </span>
                <span style={{ marginLeft: '8px' }}>{record.order}</span>
              </div>
            </td>
          )
        }
        return child
      })}
    </tr>
  )
}

const OTAUpgrade: React.FC = () => {
  const [upgradeItems, setUpgradeItems] = useState<OTAUpgradeItem[]>([])
  const [vehicleUpgradeStatus, setVehicleUpgradeStatus] = useState<VehicleUpgradeStatus[]>([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleUpgradeStatus | null>(null)
  const [editingItem, setEditingItem] = useState<OTAUpgradeItem | null>(null)
  const [isUpgrading, setIsUpgrading] = useState(false)
  const [selectedModuleId, setSelectedModuleId] = useState<string>('')
  const [publishedFirmware, setPublishedFirmware] = useState<FirmwarePackage[]>([])
  const [loading, setLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [vehicleTableLoading, setVehicleTableLoading] = useState(false)
  const [form] = Form.useForm()

  // 获取OTA升级模块列表
  const fetchOTAUpgradeList = async () => {
    try {
      setTableLoading(true)
      const response = await otaApi.getOTAUpgradeList()
      if (response.code === 200) {
        setUpgradeItems(response.data)
      } else {
        message.error(response.message)
      }
    } catch (error) {
      message.error('获取升级列表失败')
      console.error('获取升级列表失败:', error)
    } finally {
      setTableLoading(false)
    }
  }

  // 获取已发布的固件列表
  const fetchPublishedFirmware = async () => {
    try {
      const response = await firmwareApi.getPublishedFirmwareList()
      if (response.code === 200) {
        setPublishedFirmware(response.data)
      } else {
        message.error(response.message)
      }
    } catch (error) {
      message.error('获取固件列表失败')
      console.error('获取固件列表失败:', error)
    }
  }

  // 获取目标车辆列表
  const fetchTargetVehicles = async () => {
    try {
      setVehicleTableLoading(true)
      const deviceResponse = await deviceApi.getActiveDeviceList()
      if (deviceResponse.code === 200) {
        const targetVehicles: VehicleUpgradeStatus[] = deviceResponse.data.map((device: any) => ({
          id: device.id,
          vehicleId: device.vehicleId,
          batch: device.batch,
          description: device.description,
          overallProgress: 0,
          status: 'Pending' as const,
          moduleProgresses: upgradeItems.map(item => ({
            moduleId: item.id,
            moduleName: item.moduleName,
            currentVersion: item.currentVersion,
            upgradeVersion: item.upgradeVersion,
            progress: 0,
            status: 'Pending' as const,
          })),
        }))
        setVehicleUpgradeStatus(targetVehicles)
      } else {
        message.error('获取目标车辆失败：' + deviceResponse.message)
      }
    } catch (error) {
      message.error('获取目标车辆失败')
      console.error('获取目标车辆失败:', error)
    } finally {
      setVehicleTableLoading(false)
    }
  }

  useEffect(() => {
    fetchOTAUpgradeList()
    fetchPublishedFirmware()
  }, [])

  // 当升级模块列表变化时，更新目标车辆的模块进度
  useEffect(() => {
    if (upgradeItems.length > 0) {
      fetchTargetVehicles()
    }
  }, [upgradeItems])

  // 获取模块选项（从已发布的固件中提取唯一模块）
  const getModuleOptions = () => {
    const uniqueModules = publishedFirmware.reduce(
      (acc, item) => {
        if (!acc.find(m => m.moduleId === item.moduleId)) {
          acc.push({
            value: item.moduleId,
            label: item.moduleName,
            moduleId: item.moduleId,
            moduleName: item.moduleName,
          })
        }
        return acc
      },
      [] as Array<{ value: string; label: string; moduleId: string; moduleName: string }>
    )

    return uniqueModules
  }

  // 根据模块ID获取可用的升级版本
  const getUpgradeVersionOptions = (moduleId: string) => {
    return publishedFirmware
      .filter(item => item.moduleId === moduleId)
      .map(item => ({
        value: item.version,
        label: `v${item.version}`,
      }))
  }

  const moveRow = async (dragIndex: number, hoverIndex: number) => {
    const dragItem = upgradeItems[dragIndex]
    const newItems = [...upgradeItems]
    newItems.splice(dragIndex, 1)
    newItems.splice(hoverIndex, 0, dragItem)

    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }))

    setUpgradeItems(updatedItems)

    try {
      await otaApi.updateOTAUpgradeOrder(updatedItems)
    } catch (error) {
      console.error('更新排序失败:', error)
    }
  }

  const handleAdd = () => {
    setEditingItem(null)
    setSelectedModuleId('')
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEdit = (record: OTAUpgradeItem) => {
    setEditingItem(record)
    const moduleFromFirmware = publishedFirmware.find(f => f.moduleName === record.moduleName)
    setSelectedModuleId(moduleFromFirmware?.moduleId || '')
    form.setFieldsValue(record)
    setIsModalVisible(true)
  }

  const handleDelete = async (id: string) => {
    try {
      setLoading(true)
      const response = await otaApi.deleteOTAUpgradeItem(id)
      if (response.code === 200) {
        message.success(response.message)
        await fetchOTAUpgradeList()
      } else {
        message.error(response.message)
      }
    } catch (error) {
      message.error('删除失败')
      console.error('删除失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      if (editingItem) {
        const response = await otaApi.updateOTAUpgradeItem(editingItem.id, values)
        if (response.code === 200) {
          message.success(response.message)
          await fetchOTAUpgradeList()
        } else {
          message.error(response.message)
        }
      } else {
        const response = await otaApi.addOTAUpgradeItem(values)
        if (response.code === 200) {
          message.success(response.message)
          await fetchOTAUpgradeList()
        } else {
          message.error(response.message)
        }
      }

      setIsModalVisible(false)
      form.resetFields()
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    setSelectedModuleId('')
    form.resetFields()
  }

  const handleModuleChange = (value: string) => {
    const selectedModuleOption = getModuleOptions().find(option => option.value === value)
    if (selectedModuleOption) {
      const moduleLatestFirmware = publishedFirmware
        .filter(f => f.moduleId === selectedModuleOption.moduleId)
        .sort(
          (a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
        )[0]

      setSelectedModuleId(value)
      form.setFieldsValue({
        moduleName: selectedModuleOption.moduleName,
        currentVersion: moduleLatestFirmware?.version || '1.0.0',
        upgradeVersion: undefined,
      })
    }
  }

  const handleStartUpgrade = async () => {
    if (upgradeItems.length === 0) {
      Modal.warning({
        title: '提示',
        content: '请先添加升级模块后再开始升级',
        okText: '确定',
      })
      return
    }

    try {
      setIsUpgrading(true)

      const deviceResponse = await deviceApi.getActiveDeviceList()
      if (deviceResponse.code !== 200) {
        message.error('获取车辆列表失败：' + deviceResponse.message)
        return
      }

      const activeDevices = deviceResponse.data
      if (activeDevices.length === 0) {
        Modal.warning({
          title: '提示',
          content: '没有启用状态的车辆可以升级，请先在设备管理中启用车辆',
          okText: '确定',
        })
        return
      }

      const upgradeData = {
        modules: upgradeItems.map(item => ({
          id: item.id,
          moduleName: item.moduleName,
          currentVersion: item.currentVersion,
          upgradeVersion: item.upgradeVersion,
          order: item.order,
        })),
        vehicles: activeDevices.map((device: any) => ({
          id: device.id,
          vehicleId: device.vehicleId,
          batch: device.batch,
          description: device.description,
        })),
      }

      const response = await otaApi.startOTAUpgrade(upgradeData)
      if (response.code !== 200) {
        message.error(response.message)
        return
      }

      // 更新车辆升级状态
      setVehicleUpgradeStatus(response.data)
      message.success(`开始升级 ${upgradeItems.length} 个模块到 ${activeDevices.length} 辆车辆`)

      // 模拟升级过程
      await simulateUpgradeProcess(response.data, upgradeItems)
    } catch (error) {
      message.error('升级过程中发生错误')
      console.error('升级失败:', error)
    } finally {
      setIsUpgrading(false)
    }
  }

  // 模拟升级过程
  const simulateUpgradeProcess = async (
    vehicles: VehicleUpgradeStatus[],
    modules: OTAUpgradeItem[]
  ) => {
    let successCount = 0
    let failedCount = 0

    for (const vehicle of vehicles) {
      // 更新车辆状态为升级中
      setVehicleUpgradeStatus(prev =>
        prev.map(item =>
          item.id === vehicle.id ? { ...item, status: 'Upgrading' as const } : item
        )
      )

      let vehicleFailed = false

      // 模拟每个模块的升级过程
      for (let moduleIndex = 0; moduleIndex < modules.length; moduleIndex++) {
        const currentModule = modules[moduleIndex]

        // 更新当前升级的模块状态为升级中
        setVehicleUpgradeStatus(prev =>
          prev.map(item =>
            item.id === vehicle.id
              ? {
                  ...item,
                  moduleProgresses: item.moduleProgresses.map(mp =>
                    mp.moduleId === currentModule.id
                      ? {
                          ...mp,
                          status: 'Upgrading' as const,
                          startTime: new Date().toLocaleString(),
                        }
                      : mp
                  ),
                }
              : item
          )
        )

        // 模拟模块升级进度
        for (let progress = 0; progress <= 100; progress += 10) {
          await new Promise(resolve => setTimeout(resolve, 200))

          setVehicleUpgradeStatus(prev =>
            prev.map(item =>
              item.id === vehicle.id
                ? {
                    ...item,
                    moduleProgresses: item.moduleProgresses.map(mp =>
                      mp.moduleId === currentModule.id ? { ...mp, progress } : mp
                    ),
                    overallProgress: Math.round((moduleIndex * 100 + progress) / modules.length),
                  }
                : item
            )
          )
        }

        // 模拟模块升级结果（90%成功率）
        const isModuleSuccess = Math.random() > 0.1
        setVehicleUpgradeStatus(prev =>
          prev.map(item =>
            item.id === vehicle.id
              ? {
                  ...item,
                  moduleProgresses: item.moduleProgresses.map(mp =>
                    mp.moduleId === currentModule.id
                      ? {
                          ...mp,
                          status: isModuleSuccess ? ('Success' as const) : ('Failed' as const),
                          progress: isModuleSuccess ? 100 : mp.progress,
                          endTime: new Date().toLocaleString(),
                          errorMessage: isModuleSuccess ? undefined : '升级失败，请重试',
                        }
                      : mp
                  ),
                }
              : item
          )
        )

        if (!isModuleSuccess) {
          // 如果模块升级失败，整个车辆升级失败
          vehicleFailed = true
          setVehicleUpgradeStatus(prev =>
            prev.map(item =>
              item.id === vehicle.id
                ? {
                    ...item,
                    status: 'Failed' as const,
                    endTime: new Date().toLocaleString(),
                  }
                : item
            )
          )
          break
        }
      }

      // 如果没有模块失败，则车辆升级成功
      if (!vehicleFailed) {
        setVehicleUpgradeStatus(prev =>
          prev.map(item =>
            item.id === vehicle.id
              ? {
                  ...item,
                  status: 'Success' as const,
                  overallProgress: 100,
                  endTime: new Date().toLocaleString(),
                }
              : item
          )
        )
        successCount++
      } else {
        failedCount++
      }
    }

    // 显示最终结果
    if (failedCount === 0) {
      message.success(`所有 ${vehicles.length} 辆车辆升级完成`)
    } else {
      message.warning(`升级完成：${successCount} 辆成功，${failedCount} 辆失败`)
    }
  }

  // 重试车辆升级
  const handleRetryVehicle = async (vehicle: VehicleUpgradeStatus) => {
    try {
      setLoading(true)

      const response = await otaApi.retryVehicleUpgrade(vehicle.id)
      if (response.code === 200) {
        setVehicleUpgradeStatus(prev =>
          prev.map(item => (item.id === vehicle.id ? response.data : item))
        )

        // 模拟重试升级过程
        await simulateUpgradeProcess([response.data], upgradeItems)
      } else {
        message.error(response.message)
      }
    } catch (error) {
      message.error('重试升级过程中发生错误')
      console.error('重试升级失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return '#8c8c8c'
      case 'Upgrading':
        return '#1890ff'
      case 'Success':
        return '#52c41a'
      case 'Failed':
        return '#ff4d4f'
      default:
        return '#8c8c8c'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending':
        return '待升级'
      case 'Upgrading':
        return '升级中'
      case 'Success':
        return '成功'
      case 'Failed':
        return '失败'
      default:
        return '未知'
    }
  }

  // 显示车辆详情
  const handleShowVehicleDetail = (vehicle: VehicleUpgradeStatus) => {
    setSelectedVehicle(vehicle)
    setIsDetailModalVisible(true)
  }

  // 关闭车辆详情模态框
  const handleDetailModalCancel = () => {
    setIsDetailModalVisible(false)
    setSelectedVehicle(null)
  }

  // 获取当前选中车辆的最新状态
  const getCurrentVehicleStatus = () => {
    if (!selectedVehicle) return null
    return (
      vehicleUpgradeStatus.find(vehicle => vehicle.id === selectedVehicle.id) || selectedVehicle
    )
  }

  // 移除目标车辆
  const handleRemoveVehicle = (vehicleId: string) => {
    setVehicleUpgradeStatus(prev => prev.filter(vehicle => vehicle.id !== vehicleId))
    message.success('已移除目标车辆')
  }

  // 升级模块表格列定义
  const moduleColumns = [
    {
      title: '排序',
      dataIndex: 'order',
      key: 'order',
      width: 80,
      render: (order: number) => <span>{order}</span>,
    },
    {
      title: '模块名称',
      dataIndex: 'moduleName',
      key: 'moduleName',
      width: 150,
    },
    {
      title: '当前版本',
      dataIndex: 'currentVersion',
      key: 'currentVersion',
      width: 120,
    },
    {
      title: '升级版本',
      dataIndex: 'upgradeVersion',
      key: 'upgradeVersion',
      width: 120,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: OTAUpgradeItem) => (
        <Space size='small'>
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={isUpgrading || loading}
          >
            编辑
          </Button>
          <Popconfirm
            title='确定要删除这个升级模块吗？'
            onConfirm={() => handleDelete(record.id)}
            okText='确定'
            cancelText='取消'
            disabled={isUpgrading || loading}
          >
            <Button type='link' danger icon={<DeleteOutlined />} disabled={isUpgrading || loading}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // 目标车辆表格列定义
  const vehicleColumns = [
    {
      title: '车辆ID',
      dataIndex: 'vehicleId',
      key: 'vehicleId',
      width: 120,
    },
    {
      title: '批次',
      dataIndex: 'batch',
      key: 'batch',
      width: 120,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 150,
      ellipsis: true,
    },
    {
      title: '总体进度',
      dataIndex: 'overallProgress',
      key: 'overallProgress',
      width: 150,
      render: (progress: number, record: VehicleUpgradeStatus) => (
        <div style={{ width: '120px', display: 'flex', alignItems: 'center' }}>
          <Progress
            percent={progress}
            size='small'
            strokeColor={getStatusColor(record.status)}
            status={record.status === 'Failed' ? 'exception' : 'normal'}
            showInfo={false}
          />
          <span style={{ marginLeft: '8px', minWidth: '35px', fontSize: '12px', color: '#666' }}>
            {progress}%
          </span>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <span
          className={`status-${status.toLowerCase()}`}
          style={{ color: getStatusColor(status) }}
        >
          {getStatusText(status)}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: VehicleUpgradeStatus) => (
        <Space size='small'>
          <Button
            type='link'
            icon={<EyeOutlined />}
            onClick={() => handleShowVehicleDetail(record)}
            style={{ color: '#1890ff' }}
            size='small'
          >
            详情
          </Button>
          <Popconfirm
            title='确定要移除这辆目标车辆吗？'
            description='移除后该车辆将不再参与升级'
            onConfirm={() => handleRemoveVehicle(record.id)}
            okText='确定'
            cancelText='取消'
            disabled={isUpgrading || record.status === 'Upgrading'}
          >
            <Button
              type='link'
              icon={<MinusCircleOutlined />}
              disabled={isUpgrading || record.status === 'Upgrading'}
              style={{ color: '#ff4d4f' }}
              size='small'
            >
              移除
            </Button>
          </Popconfirm>
          {record.status === 'Failed' && (
            <Button
              type='link'
              icon={<RedoOutlined />}
              onClick={() => handleRetryVehicle(record)}
              disabled={isUpgrading || loading}
              style={{ color: '#52c41a' }}
              size='small'
            >
              重试
            </Button>
          )}
        </Space>
      ),
    },
  ]

  const components = {
    body: {
      row: (props: any) => {
        const index = upgradeItems.findIndex(item => item.id === props['data-row-key'])
        const record = upgradeItems[index]

        if (index === -1 || !record) {
          return <tr {...props} />
        }

        return <DraggableRow index={index} record={record} moveRow={moveRow} {...props} />
      },
    },
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Spin spinning={loading}>
        <div className='ota-upgrade'>
          {/* 升级模块管理 */}
          <div className='ota-upgrade-header'>
            <Title level={4}>升级模块管理</Title>
            <Space>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                onClick={handleAdd}
                disabled={isUpgrading}
              >
                新增升级模块
              </Button>
              <Button
                type='primary'
                danger
                icon={<RocketOutlined />}
                onClick={handleStartUpgrade}
                loading={isUpgrading}
              >
                {isUpgrading ? '升级中...' : '开始升级'}
              </Button>
            </Space>
          </div>

          <Card className='ota-upgrade-card' style={{ marginBottom: '24px' }}>
            <div className='drag-tip'>
              <Typography.Text>💡 提示：拖拽左侧图标可调整升级顺序</Typography.Text>
            </div>

            <Table
              columns={moduleColumns}
              dataSource={upgradeItems}
              rowKey='id'
              components={components}
              loading={tableLoading}
              pagination={false}
            />
          </Card>

          {/* 目标车辆 */}
          <div className='vehicle-upgrade-header'>
            <Title level={4}>目标车辆</Title>
          </div>

          <Card className='vehicle-upgrade-card'>
            <Table
              columns={vehicleColumns}
              dataSource={vehicleUpgradeStatus}
              rowKey='id'
              loading={vehicleTableLoading}
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: total => `共 ${total} 辆目标车辆`,
              }}
            />
          </Card>

          <Modal
            title={editingItem ? '编辑升级模块' : '新增升级模块'}
            open={isModalVisible}
            onOk={handleModalOk}
            onCancel={handleModalCancel}
            width={600}
            okText='确定'
            cancelText='取消'
            confirmLoading={loading}
          >
            <Form form={form} layout='vertical'>
              <Form.Item
                name='moduleName'
                label='模块名称'
                rules={[{ required: true, message: '请选择模块名称' }]}
              >
                <Select
                  placeholder='请选择模块'
                  value={selectedModuleId}
                  onChange={handleModuleChange}
                  options={getModuleOptions()}
                />
              </Form.Item>

              <Form.Item
                name='currentVersion'
                label='当前版本'
                rules={[{ required: true, message: '请输入当前版本' }]}
              >
                <Input placeholder='例如: 1.0.0' />
              </Form.Item>

              <Form.Item
                name='upgradeVersion'
                label='升级版本'
                rules={[{ required: true, message: '请输入升级版本' }]}
              >
                <Select
                  options={getUpgradeVersionOptions(selectedModuleId)}
                  placeholder='请选择升级版本'
                />
              </Form.Item>
            </Form>
          </Modal>

          {/* 车辆详情模态框 */}
          <Modal
            title={`车辆详情 - ${selectedVehicle?.vehicleId}`}
            open={isDetailModalVisible}
            onCancel={handleDetailModalCancel}
            footer={[
              <Button key='close' onClick={handleDetailModalCancel}>
                关闭
              </Button>,
            ]}
            width={800}
          >
            {(() => {
              const currentVehicle = getCurrentVehicleStatus()
              return (
                currentVehicle && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
                        <div>
                          <strong>车辆ID：</strong>
                          <span>{currentVehicle.vehicleId}</span>
                        </div>
                        <div>
                          <strong>批次：</strong>
                          <span>{currentVehicle.batch}</span>
                        </div>
                        <div>
                          <strong>状态：</strong>
                          <span style={{ color: getStatusColor(currentVehicle.status) }}>
                            {getStatusText(currentVehicle.status)}
                          </span>
                        </div>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <strong>描述：</strong>
                        <span>{currentVehicle.description}</span>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <strong>总体进度：</strong>
                        <div style={{ width: '300px', marginTop: '8px' }}>
                          <Progress
                            percent={currentVehicle.overallProgress}
                            strokeColor={getStatusColor(currentVehicle.status)}
                            status={currentVehicle.status === 'Failed' ? 'exception' : 'normal'}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 style={{ marginBottom: '16px', color: '#1f2937', fontWeight: 600 }}>
                        模块升级进度
                      </h4>
                      <div className='module-progress-container'>
                        {currentVehicle.moduleProgresses.map(module => (
                          <div
                            key={module.moduleId}
                            className='module-progress-item'
                            style={{
                              padding: '16px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              marginBottom: '12px',
                              backgroundColor: '#fafafa',
                            }}
                          >
                            <div
                              className='module-progress-header'
                              style={{ marginBottom: '12px' }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <span
                                  className='module-name'
                                  style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}
                                >
                                  {module.moduleName}
                                </span>
                                <span
                                  className='module-status'
                                  style={{
                                    color: getStatusColor(module.status),
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                  }}
                                >
                                  {getStatusText(module.status)}
                                </span>
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                {module.currentVersion} → {module.upgradeVersion}
                              </div>
                            </div>
                            <Progress
                              percent={module.progress}
                              strokeColor={getStatusColor(module.status)}
                              status={module.status === 'Failed' ? 'exception' : 'normal'}
                            />
                            {module.errorMessage && (
                              <div
                                style={{
                                  marginTop: '8px',
                                  color: '#ef4444',
                                  fontSize: '12px',
                                  backgroundColor: '#fef2f2',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                }}
                              >
                                {module.errorMessage}
                              </div>
                            )}
                            {module.startTime && (
                              <div
                                style={{
                                  marginTop: '8px',
                                  fontSize: '12px',
                                  color: '#6b7280',
                                  display: 'flex',
                                  gap: '16px',
                                }}
                              >
                                <span>开始时间: {module.startTime}</span>
                                {module.endTime && <span>结束时间: {module.endTime}</span>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              )
            })()}
          </Modal>
        </div>
      </Spin>
    </DndProvider>
  )
}

export default OTAUpgrade
